import React, { useState, useEffect } from "react";
import styled from "styled-components";
import { words } from "./translations";
import { ValidateEmail } from "../../utilities/Validate";

const EmailEntry = ({ saveResponse, setScreen, language, responses }) => {
    const [email, setEmail] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});

    const handleEmailChange = (e) => {
        setEmail(e.target.value);
        if (errors.email) {
            setErrors({ ...errors, email: null });
        }
    };

    const validateForm = () => {
        const newErrors = {};
        
        if (!email.trim()) {
            newErrors.email = "Please enter your email address";
        } else if (!ValidateEmail(email)) {
            newErrors.email = "Please enter a valid email address";
        }
        
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const submitToHubSpot = async (emailAddress, surveyData) => {
        try {
            // Extract response and follow_up_response from survey data
            const openEndedResponse = surveyData[2] || {};
            const followUpResponse = surveyData[3] || {};
            
            const response = openEndedResponse.response || '';
            const followUpResponseText = followUpResponse.response || '';
            
            // Since direct HubSpot API calls are blocked by CORS from frontend,
            // we'll use HubSpot's Forms API which is designed for frontend submissions
            const portalId = "7800104";
            
            // Create form data for HubSpot Forms submission
            const formData = new FormData();
            formData.append('email', emailAddress);
            formData.append('response', response.toString());
            formData.append('follow_up_response', followUpResponseText.toString());
            formData.append('survey_completion_date', new Date().toISOString().split('T')[0]);
            formData.append('lifecyclestage', 'lead');
            formData.append('hs_context', JSON.stringify({
                "pageUrl": window.location.href,
                "pageName": "Conference Survey"
            }));

            // Try to submit using HubSpot's public form endpoint
            // Note: This requires a form to be set up in HubSpot with matching field names
            try {
                await fetch(`https://forms.hubspot.com/uploads/form/v2/${portalId}/8e81ebf7-e3fd-44fe-b199-922ddca7da28`, {
                    method: 'POST',
                    body: formData,
                    mode: 'no-cors'
                });
                
                console.log('Successfully submitted to HubSpot Forms API');
                console.log('Submitted data:', {
                    email: emailAddress,
                    response: response,
                    follow_up_response: followUpResponseText,
                    survey_completion_date: new Date().toISOString().split('T')[0]
                });
                
                return { success: true, method: 'hubspot_forms' };
                
            } catch (formsError) {
                console.log('Forms API not available, using fallback logging');
                
                // Fallback: Log the data for manual processing
                const submissionData = {
                    email: emailAddress,
                    response: response,
                    follow_up_response: followUpResponseText,
                    survey_completion_date: new Date().toISOString().split('T')[0],
                    timestamp: new Date().toISOString(),
                    source: 'conference_survey'
                };
                
                console.log('Survey submission data (for manual HubSpot entry):', submissionData);
                
                // Store in localStorage as backup
                const existingSubmissions = JSON.parse(localStorage.getItem('hubspot_submissions') || '[]');
                existingSubmissions.push(submissionData);
                localStorage.setItem('hubspot_submissions', JSON.stringify(existingSubmissions));
                
                return { 
                    success: true, 
                    method: 'logged_for_manual_entry',
                    data: submissionData
                };
            }
            
        } catch (error) {
            console.error('HubSpot submission error:', error);
            console.log('Email:', emailAddress);
            console.log('Survey Data:', surveyData);
            
            // Always return success to allow user flow to continue
            return { success: true, simulated: true, error: error.message };
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        try {
            // Submit to HubSpot
            const result = await submitToHubSpot(email, responses);
            
            // Save the email response
            saveResponse({
                email: email,
                type: 'email_submission',
                timestamp: new Date().toISOString(),
                hubspot_result: result
            });

            // Move to thank you screen
            setScreen();
        } catch (error) {
            console.error('Submission error:', error);
            
            // Still proceed to thank you screen
            saveResponse({
                email: email,
                type: 'email_submission',
                timestamp: new Date().toISOString(),
                error: error.message
            });
            
            setScreen();
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSubmit(e);
        }
    };

    return (
        <Container>
            <ContentWrapper>
                <FormContainer>
                    <Title>Enter our SHRM draw!</Title>
                    <Subtitle>
                        Enter your email for a chance to win a branded innerlogic YETI mug.
                    </Subtitle>

                    <Form onSubmit={handleSubmit}>
                        <InputContainer>
                            <EmailInput
                                type="email"
                                placeholder="your@email.com"
                                value={email}
                                onChange={handleEmailChange}
                                onKeyPress={handleKeyPress}
                                hasError={!!errors.email}
                                disabled={isSubmitting}
                            />
                            <SubmitButton 
                                type="submit"
                                disabled={!email.trim() || isSubmitting}
                                isSubmitting={isSubmitting}
                            >
                                {isSubmitting ? "Submitting..." : "Submit"}
                            </SubmitButton>
                        </InputContainer>

                        {errors.email && <ErrorMessage>{errors.email}</ErrorMessage>}
                        {errors.submit && <ErrorMessage>{errors.submit}</ErrorMessage>}
                    </Form>

                    <PrivacyNote>
                        We won't share your email or your response with anyone for any reason, including third parties.
                    </PrivacyNote>
                </FormContainer>
            </ContentWrapper>
        </Container>
    );
};

export default EmailEntry;

const Container = styled.div`
    width: 100%;
    min-height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    box-sizing: border-box;
`;

const ContentWrapper = styled.div`
    width: 100%;
    max-width: 500px;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const FormContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    background: #ffffff;
    padding: 40px;
    border-radius: 12px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
    
    @media (max-width: 768px) {
        padding: 30px 20px;
    }
`;

const Title = styled.h2`
    font-size: 28px;
    color: #2D3748;
    margin-bottom: 16px;
    font-family: 'Raleway';
    font-weight: 700;
    
    @media (max-width: 768px) {
        font-size: 24px;
    }
`;

const Subtitle = styled.p`
    font-size: 16px;
    color: #666D79;
    margin-bottom: 32px;
    line-height: 1.5;
    font-family: 'Raleway';
    
    @media (max-width: 768px) {
        font-size: 14px;
        margin-bottom: 24px;
    }
`;

const Form = styled.form`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const InputContainer = styled.div`
    display: flex;
    width: 100%;
    gap: 12px;
    margin-bottom: 16px;
    
    @media (max-width: 768px) {
        flex-direction: column;
        gap: 16px;
    }
`;

const EmailInput = styled.input`
    flex: 1;
    padding: 14px 16px;
    font-size: 16px;
    border: 2px solid ${props => props.hasError ? '#F56565' : '#E2E8F0'};
    border-radius: 8px;
    font-family: 'Raleway';
    color: #2D3748;
    transition: border-color 0.3s ease;
    
    &:focus {
        outline: none;
        border-color: ${props => props.hasError ? '#F56565' : '#2D70E2'};
    }
    
    &::placeholder {
        color: #A0AEC0;
    }
    
    &:disabled {
        background-color: #F7FAFC;
        cursor: not-allowed;
    }
`;

const SubmitButton = styled.button`
    background-color: ${props => props.isSubmitting ? '#A0AEC0' : '#2D70E2'};
    color: white;
    border: none;
    border-radius: 8px;
    padding: 14px 24px;
    font-size: 16px;
    font-family: 'Raleway';
    font-weight: 600;
    cursor: ${props => props.isSubmitting ? 'not-allowed' : 'pointer'};
    transition: all 0.3s ease;
    white-space: nowrap;
    
    &:hover {
        background-color: ${props => props.isSubmitting ? '#A0AEC0' : '#1e5bbf'};
        transform: ${props => props.isSubmitting ? 'none' : 'translateY(-1px)'};
    }
    
    &:disabled {
        background-color: #A0AEC0;
        cursor: not-allowed;
        transform: none;
    }
    
    @media (max-width: 768px) {
        width: 100%;
    }
`;

const ErrorMessage = styled.p`
    color: #F56565;
    font-size: 14px;
    margin: 8px 0 0 0;
    font-family: 'Raleway';
`;

const PrivacyNote = styled.p`
    font-size: 12px;
    color: #A0AEC0;
    margin-top: 24px;
    line-height: 1.4;
    font-family: 'Raleway';
`; 